import { OBJECT_POLICY_ACTIONS } from './rbacConstants';
import { formatFeatureLabel } from './rbacMappers';
import { normalizeObjectGrantSideEffects } from './objectPermissionRules';
import { normalizeFeatureGrantSideEffects } from './featurePermissionRules';

export function collectNodeGrantTargets(node) {
  const features = [];
  const objects = [];

  (node?.features || []).forEach((feature) => {
    features.push(feature.codename);
  });

  (node?.objects || []).forEach((objectDef) => {
    objects.push({
      model: objectDef.model,
      actions: OBJECT_POLICY_ACTIONS.map((action) => action.key),
    });
  });

  (node?.children || []).forEach((child) => {
    const childTargets = collectNodeGrantTargets(child);
    features.push(...childTargets.features);
    objects.push(...childTargets.objects);
  });

  return { features, objects };
}

export function getNodeToggleState(node, grants = {}) {
  const { features, objects } = collectNodeGrantTargets(node);
  let grantedCount = 0;
  let totalCount = 0;

  features.forEach((codename) => {
    totalCount += 1;
    if (grants.features?.[codename]) {
      grantedCount += 1;
    }
  });

  objects.forEach(({ model, actions }) => {
    const modelKey = (model || '').toLowerCase();
    actions.forEach((action) => {
      totalCount += 1;
      if (grants.objects?.[modelKey]?.[action]) {
        grantedCount += 1;
      }
    });
  });

  if (totalCount === 0) {
    return { checked: false, indeterminate: false, isEmpty: true };
  }

  return {
    checked: grantedCount === totalCount,
    indeterminate: grantedCount > 0 && grantedCount < totalCount,
    isEmpty: false,
  };
}

export function buildFeatureRows(node) {
  return (node?.features || []).map((feature) => ({
    id: `feature:${feature.codename}`,
    type: 'feature',
    codename: feature.codename,
    label: feature.name || formatFeatureLabel(feature.codename),
    description: feature.description,
  }));
}

export function buildObjectRows(node) {
  return (node?.objects || []).map((objectDef) => ({
    id: `object:${(objectDef.model || '').toLowerCase()}`,
    type: 'object',
    model: objectDef.model,
    label: objectDef.label || objectDef.model,
  }));
}

function collectLeafGroups(node, groups) {
  const hasChildren = Boolean(node.children?.length);
  const hasDirectContent = Boolean(node.features?.length || node.objects?.length);

  if (hasDirectContent) {
    groups.push({
      codename: node.codename,
      label: node.label,
      node,
      featureRows: buildFeatureRows(node),
      objectRows: buildObjectRows(node),
    });
  }

  if (hasChildren) {
    node.children.forEach((child) => collectLeafGroups(child, groups));
  }
}

export function buildModuleDisplayGroups(rootModule) {
  const groups = [];

  if (rootModule.features?.length || rootModule.objects?.length) {
    groups.push({
      codename: rootModule.codename,
      label: rootModule.label,
      node: rootModule,
      featureRows: buildFeatureRows(rootModule),
      objectRows: buildObjectRows(rootModule),
    });
  }

  (rootModule.children || []).forEach((child) => collectLeafGroups(child, groups));
  return groups;
}

export function buildPermissionTreeSections(catalog) {
  return (catalog?.modules || [])
    .map((module) => ({
      codename: module.codename,
      label: module.label,
      description: module.description,
      rootNode: module,
      groups: buildModuleDisplayGroups(module),
    }))
    .filter((section) => section.groups.length > 0);
}

export function applyNodeGrantPatch(grants, node, granted) {
  const { features, objects } = collectNodeGrantTargets(node);
  const nextFeatures = { ...grants.features };
  features.forEach((codename) => {
    nextFeatures[codename] = Boolean(granted);
  });

  const nextObjects = { ...grants.objects };
  objects.forEach(({ model, actions }) => {
    const modelKey = (model || '').toLowerCase();
    nextObjects[modelKey] = { ...(nextObjects[modelKey] || {}) };
    actions.forEach((action) => {
      nextObjects[modelKey][action] = Boolean(granted);
    });
  });

  return normalizeFeatureGrantSideEffects(
    normalizeObjectGrantSideEffects({
      features: nextFeatures,
      objects: nextObjects,
      resources: grants.resources || { accounts: [], campaigns: [] },
      record_actions: grants.record_actions || {},
    })
  );
}

export function flattenSectionRows(section) {
  const rows = [];
  (section.groups || []).forEach((group) => {
    group.featureRows.forEach((row) => {
      rows.push({ ...row, groupLabel: group.label });
    });
    group.objectRows.forEach((row) => {
      rows.push({ ...row, groupLabel: group.label });
    });
  });
  return rows;
}
